import { Link } from "react-router-dom";

import { BreakdownSection } from "@/components/stats/BreakdownSection";
import { StatsOverview } from "@/components/stats/StatsOverview";
import { StreakDisplay } from "@/components/stats/StreakDisplay";
import { formatLabel } from "@/constants/filters";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useStats, useStatsBreakdown } from "@/hooks/use-stats";
import type { BreakdownBucket } from "@/types";
import { getErrorMessage } from "@/utils/error-message";

function findBest(buckets: BreakdownBucket[]): BreakdownBucket | null {
  const qualified = buckets.filter((b) => b.total >= 2);
  if (qualified.length === 0) return null;
  return qualified.reduce((a, b) => (a.accuracy > b.accuracy ? a : b));
}

function findWeakest(buckets: BreakdownBucket[]): BreakdownBucket | null {
  const qualified = buckets.filter((b) => b.total >= 2);
  if (qualified.length === 0) return null;
  return qualified.reduce((a, b) => (a.accuracy < b.accuracy ? a : b));
}

export function Stats() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useStats();
  const { data: breakdown, isLoading: breakdownLoading, error: breakdownError } = useStatsBreakdown();

  const isLoading = statsLoading || breakdownLoading;
  const error = statsError || breakdownError;

  const bestCategory = breakdown ? findBest(breakdown.byCategory) : null;
  const weakestCategory = breakdown ? findWeakest(breakdown.byCategory) : null;
  const bestPosition = breakdown ? findBest(breakdown.byPosition) : null;
  const weakestPosition = breakdown ? findWeakest(breakdown.byPosition) : null;

  const hasInsights = bestCategory || weakestCategory || bestPosition || weakestPosition;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-navy-700">Your Stats</h1>

      {isLoading && <LoadingSpinner message="Loading stats..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}

      {stats && <StatsOverview stats={stats} />}

      {stats && (
        <Card className="flex items-center justify-between">
          <StreakDisplay
            streak={stats.streak}
            bestStreak={stats.bestStreak}
            dailyStreak={stats.dailyStreak}
            bestDailyStreak={stats.bestDailyStreak}
            streakFreezeAvailable={stats.streakFreezeAvailable}
          />
        </Card>
      )}

      {breakdown && hasInsights && (
        <Card>
          <h3 className="mb-3 text-sm font-medium uppercase tracking-wider text-gray-500">
            Insights
          </h3>
          <div className="space-y-2">
            {bestCategory && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Strongest category:</span>{" "}
                {formatLabel(bestCategory.label)} at {bestCategory.accuracy}%
                ({bestCategory.correct}/{bestCategory.total})
              </p>
            )}
            {weakestCategory && weakestCategory !== bestCategory && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-red-700">Needs work:</span>{" "}
                {formatLabel(weakestCategory.label)} at {weakestCategory.accuracy}%
                ({weakestCategory.correct}/{weakestCategory.total})
              </p>
            )}
            {bestPosition && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Best position:</span>{" "}
                {bestPosition.label} at {bestPosition.accuracy}%
                ({bestPosition.correct}/{bestPosition.total})
              </p>
            )}
            {weakestPosition && weakestPosition !== bestPosition && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-red-700">Weakest position:</span>{" "}
                {weakestPosition.label} at {weakestPosition.accuracy}%
                ({weakestPosition.correct}/{weakestPosition.total})
              </p>
            )}
          </div>
        </Card>
      )}

      {breakdown && (
        <>
          <BreakdownSection
            title="By Category"
            buckets={breakdown.byCategory}
          />
          <BreakdownSection
            title="By Difficulty"
            buckets={breakdown.byDifficulty}
          />
          <BreakdownSection
            title="By Position"
            buckets={breakdown.byPosition}
          />
        </>
      )}

      {breakdown && !hasInsights && (
        <Card className="text-center">
          <p className="text-gray-500">
            Answer more questions to unlock detailed insights!
          </p>
        </Card>
      )}

      <Link
        to="/progress"
        className="block min-h-[44px] text-center text-sm font-medium leading-[44px] text-navy-600 hover:text-navy-800"
      >
        View Answer History &rarr;
      </Link>
    </div>
  );
}
